import { Injectable, computed, effect, inject, signal, untracked, DOCUMENT } from '@angular/core';

import { BrowserStorageService } from '@spaces-ui/browser-storage';

import { ThemeIntrospectionService } from './theme-introspection';
import { ThemeMode } from './theme-introspection.util';
import { ThemePersistencePort } from './theme-persistence';

/**
 * The appearance mode a user can pick: an explicit mode, or follow the OS.
 * Deliberately lowercase (exempt from the PascalCase literal convention): these
 * are wire values persisted to localStorage and matched against CSS theme-mode
 * markers; the PascalCase API-facing type is `ThemeAppearanceMode` in rest-api.
 */
export type AppearanceMode = 'light' | 'dark' | 'system';

/** localStorage keys: family + mode are the source of truth; `theme` mirrors the
 * resolved concrete class for backwards-compatible readers (embed, other tabs). */
const FAMILY_KEY = 'theme-family';
const MODE_KEY = 'theme-mode';
const LEGACY_THEME_KEY = 'theme';

/** Fallback family/concrete classes used before any theme is discovered. */
const DEFAULT_FAMILY = 'magpie';
const NEUTRAL_LIGHT_CLASS = 'sunshine';
const NEUTRAL_DARK_CLASS = 'moonshine';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private document = inject(DOCUMENT);
  private readonly introspection = inject(ThemeIntrospectionService);

  /**
   * Optional remote-persistence sink. Registered by the lazy-loaded chat feature
   * once it loads (post-auth); null otherwise — so unauthenticated screens and apps
   * that don't include the feature (meadow-docs, embed) sync to `localStorage` only.
   */
  private persistence: ThemePersistencePort | null = null;

  /**
   * The chosen theme family (the "bird"/hue identity, e.g. `flamingo`). Its
   * light/dark variants are picked between by {@link mode}.
   */
  public readonly family = signal(DEFAULT_FAMILY);

  /**
   * Family forced for this window only (e.g. the popped-out call window pins
   * `magpie`). While set, resolution ignores {@link family} and persistence is
   * suspended, so the force can never leak into the localStorage preference
   * shared with other windows.
   */
  private forcedFamily: string | null = null;

  /** The chosen appearance mode: an explicit `light`/`dark`, or follow the OS. */
  public readonly mode = signal<AppearanceMode>('light');

  /**
   * The concrete theme class currently applied to the body (e.g. `sunshine`,
   * `flamingo`, `flamingo-dark`). Kept as the public read surface many consumers
   * already use; derived from {@link family} + {@link effectiveMode}.
   */
  public readonly theme = signal(NEUTRAL_LIGHT_CLASS);

  /** Tracks the OS `prefers-color-scheme` so {@link effectiveMode} reacts to it. */
  private readonly prefersDarkQuery =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;
  private readonly systemPrefersDark = signal(this.prefersDarkQuery?.matches ?? false);

  /** The resolved light/dark mode after applying the OS preference for `system`. */
  public readonly effectiveMode = computed<ThemeMode>(() => {
    const mode = this.mode();
    if (mode === 'system') {
      return this.systemPrefersDark() ? 'dark' : 'light';
    }
    return mode;
  });

  /** Whether the active appearance resolves to dark. The correct signal for
   * consumers choosing light/dark assets (replaces `theme() === 'moonshine'`). */
  public readonly isDark = computed(() => this.effectiveMode() === 'dark');

  /**
   * The neutral (magpie) concrete class for the current mode. For UI that pins
   * itself to the neutral family regardless of the active one — e.g. the calls
   * UI applies this to its overlay-attached surfaces (menus, emoji panel).
   */
  public readonly neutralClass = computed(() =>
    this.isDark() ? NEUTRAL_DARK_CLASS : NEUTRAL_LIGHT_CLASS,
  );

  /**
   * True while an in-memory theme authored in the playground is applied via
   * inline CSS custom property overrides. Used by the appearance UI to show
   * "no theme selected". Intentionally not persisted across reloads.
   */
  public customThemeActive = signal(false);

  /** CSS custom property names currently overridden by a custom theme. */
  private customThemeKeys: string[] = [];

  /**
   * Opaque editor snapshot stored by the theme playground so its form can be
   * restored after the settings dialog is closed and reopened while a custom
   * theme is still applied. The playground owns the shape; this service only
   * keeps it tied to the custom-theme lifecycle.
   */
  private customThemeDraft: unknown = null;

  /**
   * A theme id applied by {@link setTheme} while discovery was still empty, so its
   * family/mode could not be derived. Re-run once discovery lands — see the
   * constructor effect.
   */
  private unresolvedThemeId: string | null = null;

  public constructor() {
    this.initializeTheme();

    // Theme discovery reads the compiled stylesheet, which production serves as a
    // render-blocking `<link>` that is still fetched + parsed asynchronously. Until
    // it lands, `resolveThemeId` finds nothing and `resolveThemeClass` falls back to
    // the neutral class — so re-resolve once discovery reports themes, or a user
    // with a stored bird family renders neutral for the rest of the session. The
    // authenticated app happens to self-heal when settings arrive post-auth; the
    // login/signup screens, meadow-docs and the embed never do.
    effect(() => {
      const discovered = this.introspection.themes();
      if (discovered.length === 0) {
        return;
      }
      // Everything below reads and writes theme state. Untracked so this effect
      // depends only on discovery — family/mode changes already re-apply directly,
      // and tracking `theme()` here would make the effect retrigger itself.
      untracked(() => {
        if (this.customThemeActive()) {
          return;
        }
        // An id applied before discovery was ready never got resolved to a
        // family/mode pair, so re-run it now rather than resolving from state.
        const pending = this.unresolvedThemeId;
        if (pending) {
          this.unresolvedThemeId = null;
          this.setTheme(pending);
          return;
        }
        const resolved = this.resolveThemeClass();
        if (resolved !== this.theme()) {
          this.applyThemeClass(resolved);
        }
      });
    });

    // React to OS light/dark changes while following the system preference.
    this.prefersDarkQuery?.addEventListener('change', event => {
      this.systemPrefersDark.set(event.matches);
      if (this.mode() === 'system') {
        this.applyResolvedTheme();
      }
    });

    // Sync from other tabs. Either the new family/mode keys or the legacy
    // resolved-theme key may change; re-read and re-apply from storage.
    window.addEventListener('storage', storageEvent => {
      if (
        storageEvent.key === MODE_KEY ||
        storageEvent.key === FAMILY_KEY ||
        storageEvent.key === LEGACY_THEME_KEY
      ) {
        this.clearCustomTheme();
        this.restoreFromStorage();
      }
    });
  }

  /**
   * Pin this window's theme family regardless of the stored / server-synced
   * preference. Mode (light / dark / system) still follows the user. Every
   * re-apply path (storage events, server hydration, OS scheme changes) funnels
   * through {@link resolveThemeClass}, so the pin survives them all; the
   * {@link family} and {@link mode} signals keep reporting the user's real values.
   */
  public forceFamily(family: string): void {
    this.forcedFamily = family;
    this.applyResolvedTheme();
  }

  /** Switch the theme family (hue), keeping the current mode. */
  public setFamily(family: string): void {
    this.clearCustomTheme();
    this.family.set(family);
    this.applyResolvedTheme();
    this.persistRemote();
  }

  /** Switch the appearance mode (light / dark / system), keeping the family. */
  public setMode(mode: AppearanceMode): void {
    this.clearCustomTheme();
    this.mode.set(mode);
    this.applyResolvedTheme();
    this.persistRemote();
  }

  /** Flip between an explicit light and dark mode within the current family. */
  public toggleTheme(): void {
    this.setMode(this.effectiveMode() === 'dark' ? 'light' : 'dark');
  }

  /** Register the remote-persistence sink (called by the chat feature on load). */
  public registerPersistence(port: ThemePersistencePort): void {
    this.persistence = port;
  }

  /**
   * Apply a family + mode loaded from the server. Updates the live state and the
   * `localStorage` cache, but does **not** echo back to the remote port — this is
   * the hydration path, so a server value never round-trips straight back.
   */
  public applyFromServer(family: string, mode: AppearanceMode): void {
    this.clearCustomTheme();
    this.family.set(family);
    this.mode.set(mode);
    this.applyResolvedTheme();
  }

  /**
   * Fallback used when an authenticated org has no saved theme: adopt the user's
   * legacy global preference from the read-only `theme` key (`moonshine` → neutral
   * dark, otherwise neutral light). Deliberately does not inherit the last-used
   * family from another org. Like {@link applyFromServer}, does not echo remotely.
   */
  public applyLegacyGlobalTheme(): void {
    const legacy = BrowserStorageService.getLocalStorageItem(LEGACY_THEME_KEY);
    this.applyFromServer(DEFAULT_FAMILY, legacy === NEUTRAL_DARK_CLASS ? 'dark' : 'light');
  }

  /** Persist the current theme to every org the user belongs to (via the port). */
  public applyToAllOrgs(): void {
    // Like persistRemote: the port surfaces save failures to the user itself,
    // so a rejected save is swallowed rather than becoming an unhandled rejection.
    void this.persistence?.saveThemeForAllOrgs(this.family(), this.mode()).catch(() => undefined);
  }

  /** Push the current selection to the remote port, if one is provided. */
  private persistRemote(): void {
    // The port surfaces save failures to the user itself (error toast) and the
    // local theme state is already applied, so a rejected save is swallowed here
    // rather than becoming an unhandled promise rejection.
    void this.persistence?.saveThemeForCurrentOrg(this.family(), this.mode()).catch(() => undefined);
  }

  /**
   * Apply a concrete theme class id directly (e.g. `sunshine`, `flamingo-dark`).
   * Kept for the embed SDK, which sets `sunshine`/`moonshine` by name. Resolves
   * the id to its family + mode when known so the two-axis state stays in sync.
   */
  public setTheme(themeId: string): void {
    this.clearCustomTheme();
    // The neutral classes always exist and map deterministically onto the merged
    // Magpie family — handle them without relying on a parsed stylesheet (embed
    // sets `sunshine`/`moonshine` by name, possibly before discovery is ready).
    if (themeId === NEUTRAL_LIGHT_CLASS || themeId === NEUTRAL_DARK_CLASS) {
      this.family.set(DEFAULT_FAMILY);
      this.mode.set(themeId === NEUTRAL_DARK_CLASS ? 'dark' : 'light');
      this.applyResolvedTheme();
      return;
    }
    const themes = this.introspection.getThemes();
    const info = themes.find(theme => theme.id === themeId);
    if (info) {
      this.family.set(info.family);
      this.mode.set(info.mode);
      this.applyResolvedTheme();
      return;
    }
    if (themes.length > 0) {
      // Unknown id but discovery succeeded — the id was renamed/removed. Fall
      // back to the neutral light family rather than apply an unstyled class.
      this.family.set(DEFAULT_FAMILY);
      this.mode.set('light');
      this.applyResolvedTheme();
      return;
    }
    // Discovery empty (e.g. before the stylesheet is parsed): trust the id, and
    // remember it so the constructor effect can resolve its family/mode once
    // discovery lands. Without that, state keeps whatever family/mode it had and
    // the next re-resolve would swap this class out for the wrong theme.
    this.unresolvedThemeId = themeId;
    this.applyThemeClass(themeId);
  }

  /**
   * Apply an in-memory theme by overriding CSS custom properties inline on the
   * body. Inline declarations out-specify the theme class rule, so this wins
   * over the active theme. Replaces any previously applied custom overrides.
   */
  public applyCustomTheme(overrides: Record<string, string>, draft: unknown = null) {
    this.clearCustomThemeProperties();
    const body = this.document.body;
    const keys = Object.keys(overrides);
    for (const key of keys) {
      body.style.setProperty(key, overrides[key]);
    }
    this.customThemeKeys = keys;
    this.customThemeDraft = draft;
    this.customThemeActive.set(true);
  }

  /** Remove any applied custom-theme overrides and clear the active flag. */
  public clearCustomTheme() {
    this.clearCustomThemeProperties();
    this.customThemeDraft = null;
    this.customThemeActive.set(false);
  }

  /** The editor snapshot stored by the last {@link applyCustomTheme} call. */
  public getCustomThemeDraft(): unknown {
    return this.customThemeDraft;
  }

  private clearCustomThemeProperties() {
    const body = this.document.body;
    for (const key of this.customThemeKeys) {
      body.style.removeProperty(key);
    }
    this.customThemeKeys = [];
  }

  private initializeTheme() {
    // In embed context, read theme from URL hash (available synchronously).
    // This avoids a flash of the wrong theme before the Initialize message.
    const embedTheme = this.getThemeFromUrlHash();
    if (embedTheme) {
      this.family.set(DEFAULT_FAMILY);
      this.mode.set(embedTheme === NEUTRAL_DARK_CLASS ? 'dark' : 'light');
      this.applyResolvedTheme();
      return;
    }

    this.restoreFromStorage();
  }

  /**
   * Restore the appearance from localStorage. Prefers the family/mode keys;
   * otherwise migrates the legacy single `theme` id (the only persisted values
   * real users have are `sunshine`/`moonshine`, which map onto the merged neutral
   * "Magpie" family). Falls back to neutral light for a brand-new user.
   */
  private restoreFromStorage() {
    const storedMode = BrowserStorageService.getLocalStorageItem(MODE_KEY);
    if (storedMode) {
      this.mode.set(this.normalizeMode(storedMode));
      const storedFamily = BrowserStorageService.getLocalStorageItem(FAMILY_KEY);
      this.family.set(storedFamily || DEFAULT_FAMILY);
      this.applyResolvedTheme();
      return;
    }

    const legacy = BrowserStorageService.getLocalStorageItem(LEGACY_THEME_KEY);
    if (legacy === NEUTRAL_LIGHT_CLASS || legacy === NEUTRAL_DARK_CLASS) {
      this.family.set(DEFAULT_FAMILY);
      this.mode.set(legacy === NEUTRAL_DARK_CLASS ? 'dark' : 'light');
      this.applyResolvedTheme();
      return;
    }
    if (legacy) {
      this.setTheme(legacy);
      return;
    }

    this.applyResolvedTheme();
  }

  private normalizeMode(value: string): AppearanceMode {
    return value === 'dark' || value === 'system' ? value : 'light';
  }

  /** Resolve (family, effectiveMode) to a concrete class and apply it. */
  private applyResolvedTheme() {
    this.applyThemeClass(this.resolveThemeClass());
  }

  private resolveThemeClass(): string {
    const mode = this.effectiveMode();
    const family = this.forcedFamily ?? this.family();
    return (
      this.introspection.resolveThemeId(family, mode) ??
      (mode === 'dark' ? NEUTRAL_DARK_CLASS : NEUTRAL_LIGHT_CLASS)
    );
  }

  /** Swap the body theme class to `themeClass`, update state, and persist. */
  private applyThemeClass(themeClass: string) {
    const body = this.document.body;
    const previous = this.theme();
    if (previous && previous !== themeClass) {
      body.classList.remove(previous);
    }
    body.classList.add(themeClass);
    this.theme.set(themeClass);
    this.persist();
  }

  private persist() {
    if (this.forcedFamily !== null) {
      // A forced-family window (call popout) must never write the shared keys:
      // it may hold values that are stale relative to the main window.
      return;
    }
    BrowserStorageService.setLocalStorageItem(FAMILY_KEY, this.family());
    BrowserStorageService.setLocalStorageItem(MODE_KEY, this.mode());
    // The legacy `theme` key is intentionally left read-only: it preserves the
    // user's pre-family/mode global preference (`sunshine`/`moonshine`) so it can
    // serve as the fallback for orgs with no saved theme. It is still read on
    // restore + as that fallback, and the embed host writes it (host → embed only),
    // so freezing the write here does not break legacy readers.
  }

  /**
   * Extract theme from URL hash in embed context.
   * SDK passes theme as: /embed/full#theme=sunshine
   */
  private getThemeFromUrlHash(): typeof NEUTRAL_LIGHT_CLASS | typeof NEUTRAL_DARK_CLASS | null {
    // Only check hash for embed routes
    const pathname = window.location.pathname;
    if (!pathname.includes('/embed/')) {
      return null;
    }

    const hash = window.location.hash;
    if (!hash || hash.length < 2) {
      return null;
    }

    const params = new URLSearchParams(hash.substring(1));
    const theme = params.get('theme');

    if (theme === NEUTRAL_LIGHT_CLASS) {
      return NEUTRAL_LIGHT_CLASS;
    }
    if (theme === NEUTRAL_DARK_CLASS) {
      return NEUTRAL_DARK_CLASS;
    }

    return null;
  }
}
